/* eslint-disable */
import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Tooltip, Tag, Select, Divider, Descriptions, Typography, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faSave,
  faTrash,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import usePutAdapter from "../../../../customHooks/PUT_requests/usePutAdapter";
import { RootState } from "../../../../app/store";
import {
  IconsInterface,
  ConditionDef,
  RuleDefinition,
  RuleExpr,
} from "../../../../interfaces/IconDefinitions";
import { AdapterInterface } from "../../../../interfaces/Adapter.interface";
// ICONS
import elDown from "../../../../assets/icons/elevatorStatus/el-down.svg";
import elUp from "../../../../assets/icons/elevatorStatus/el-up.svg";
import elClose from "../../../../assets/icons/elevatorSingle/el-close.svg";
import elOpen from "../../../../assets/icons/elevatorSingle/el-open.svg";
import elFrontCloseRearClose from "../../../../assets/icons/elevatorDouble/el-front-close-rear-close.svg";
import elFrontCloseRearOpen from "../../../../assets/icons/elevatorDouble/el-front-close-rear-open.svg";
import elFrontOpenRearClose from "../../../../assets/icons/elevatorDouble/el-front-open-rear-close.svg";
import elFrontOpenRearOpen from "../../../../assets/icons/elevatorDouble/el-front-open-rear-open.svg";

const { Text } = Typography;

function OrBlock({ children, theme }: { children: React.ReactNode; theme: string }) {
  return (
    <div
      style={{
        borderRadius: 12,
        padding: 10,
        marginTop: 10,
      }}
      className={`generalStyles__flex generalStyles__box__${theme}`}
    >
      <div className="generalStyles__width100">{children}</div>
    </div>
  );
}

function AndBox({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>;
}

function ConditionTooltipContent({
  conditionDef = undefined,
}: {
  // eslint-disable-next-line react/require-default-props
  conditionDef?: ConditionDef;
}) {
  if (!conditionDef) return <Text type="secondary">Condition not found</Text>;

  const c = conditionDef.condition;

  return (
    <div style={{ width: 300 }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600 }}>{conditionDef.name}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {conditionDef.id}
        </Text>
      </div>

      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Active">
          {c.active === true ? <Tag color="green">true</Tag> : <Tag color="red">false</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Message">{c.message ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Front Door">{c.frontDoor ?? "null"}</Descriptions.Item>
        <Descriptions.Item label="Rear Door">{c.rearDoor ?? "null"}</Descriptions.Item>
        <Descriptions.Item label="Direction">{c.direction ?? "null"}</Descriptions.Item>
      </Descriptions>
    </div>
  );
}

export default function IconDefinitionRules({
  adapter,
  isLoading,
}: {
  isLoading: boolean;
  adapter: AdapterInterface;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const ICONS: IconsInterface[] = [
    { id: "elUp", name: "Up", icon: elUp },
    { id: "elDown", name: "Down", icon: elDown },
    { id: "elClose", name: "door", icon: elClose },
    { id: "elOpen", name: "Open", icon: elOpen },
    {
      id: "elFrontCloseRearClose",
      name: "Front Close Rear Close",
      icon: elFrontCloseRearClose,
    },
    {
      id: "elFrontCloseRearOpen",
      name: "Front Close Rear Open",
      icon: elFrontCloseRearOpen,
    },
    {
      id: "elFrontOpenRearClose",
      name: "Front Open Rear Close",
      icon: elFrontOpenRearClose,
    },
    {
      id: "elFrontOpenRearOpen",
      name: "Front Open Rear Open",
      icon: elFrontOpenRearOpen,
    },
  ];

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [editIconId, setEditIconId] = useState<string>("");
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [selectData, setSelectData] = useState<{ label: string; value: string; icon: string }[]>(
    [],
  );
  const [selectValues, setSelectValues] = useState<string[]>([]);
  const [removeIcon] = useState<string>("");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  // eslint-disable-next-line no-unused-vars
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { putAdapter, isErrorPutAdapter, isSuccessPutAdapter, isLoadingPutAdapter } =
    usePutAdapter();

  // ************************************************ */
  // HELPERS **************************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // USE MEMO *************************************** */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  function addChildRefByConditionId(
    rules: RuleDefinition[],
    conditionId: string,
  ): RuleDefinition[] {
    return rules.map((rule: any) => {
      const existsInConditions = rule.conditions?.some((c: any) => c.id === conditionId);
      if (!existsInConditions) return rule;
      const currentActiveWhen = rule.activeWhen;
      const isOrRoot = currentActiveWhen?.kind === "group" && currentActiveWhen?.op === "OR";
      const orChildren: RuleExpr[] = isOrRoot ? currentActiveWhen.children ?? [] : [];
      const alreadyInChildren = orChildren.some(
        (ch: any) => ch?.kind === "ref" && ch?.id === conditionId,
      );
      return {
        ...rule,
        activeWhen: {
          kind: "group",
          op: "OR",
          children: alreadyInChildren
            ? orChildren
            : [...orChildren, { id: conditionId, kind: "ref" }],
        },
        removeWhen: { id: removeIcon === "" ? "" : removeIcon, kind: "ref" },
      };
    });
  }

  function addAndGroupByConditionIds(
    rules: RuleDefinition[],
    conditionIds: string[],
  ): RuleDefinition[] {
    if (!Array.isArray(conditionIds) || conditionIds.length < 2) return rules;
    return rules.map((rule: any) => {
      const idsInRule = conditionIds.filter((id) => rule.conditions?.some((c: any) => c.id === id));
      if (idsInRule.length < 2) return rule;
      const uniqueIdsInRule = Array.from(new Set(idsInRule));
      const newAndGroup: RuleExpr = {
        kind: "group",
        op: "AND",
        children: uniqueIdsInRule.map((id) => ({ kind: "ref", id })),
      };
      const currentActiveWhen = rule.activeWhen;
      const isOrRoot = currentActiveWhen?.kind === "group" && currentActiveWhen?.op === "OR";
      const orChildren: RuleExpr[] = isOrRoot ? currentActiveWhen.children ?? [] : [];
      const targetSet = new Set(uniqueIdsInRule);
      const alreadyExists = orChildren.some((ch: any) => {
        if (ch?.kind !== "group" || ch?.op !== "AND" || !Array.isArray(ch?.children)) return false;
        const refs = ch.children
          .filter((x: any) => x?.kind === "ref" && typeof x?.id === "string")
          .map((x: any) => x.id);
        if (refs.length !== targetSet.size) return false;
        const refSet = new Set(refs);
        if (refSet.size !== targetSet.size) return false;
        for (const id of targetSet) {
          if (!refSet.has(id)) return false;
        }
        return true;
      });
      if (alreadyExists) return rule;
      return {
        ...rule,
        activeWhen: {
          kind: "group",
          op: "OR",
          children: [...orChildren, newAndGroup],
        },
        removeWhen: { id: removeIcon === "" ? "" : removeIcon, kind: "ref" },
      };
    });
  }

  const onClickResetAll = () => {
    setIsResetModalOpen(true);
  };

  const onConfirmReset = () => {
    putAdapter({
      ...adapter,
      socket_message_priority: {
        rules: [],
        customConfiguration: true,
      },
    });
    setIsResetModalOpen(false);
  };

  const onClickSave = () => {
    if (selectValues.length === 1) {
      const updatedRules = addChildRefByConditionId(
        adapter.socket_message_priority.rules,
        selectValues[0],
      );
      putAdapter({
        ...adapter,
        socket_message_priority: {
          ...adapter.socket_message_priority,
          rules: updatedRules,
        },
      });
    } else {
      const updatedRules = addAndGroupByConditionIds(
        adapter.socket_message_priority.rules,
        selectValues,
      );
      putAdapter({
        ...adapter,
        socket_message_priority: {
          ...adapter.socket_message_priority,
          rules: updatedRules,
        },
      });
    }
  };

  const onChangeSelectActivateIcon = (newValues: string[]) => {
    setSelectValues(newValues);
  };

  const getCondition = (id: string, icon: string, rules: RuleDefinition[]) =>
    rules?.find((r) => r.icon === icon)?.conditions.find((c) => c.id === id);

  const getConditionName = (
    id: string,
    icon: string,
    rules: RuleDefinition[],
  ): string | undefined => {
    if (id !== "N" && icon !== "") {
      return rules?.find((r) => r.icon === icon)?.conditions.find((c) => c.id === id)?.name;
    }
    return "NO REMOVE";
  };

  const onClickDeleteItem = useCallback(
    (index: number, icon: string) => {
      const rules = adapter?.socket_message_priority?.rules ?? [];
      const indexRule = rules.findIndex((item: RuleDefinition) => item.icon === icon);
      if (indexRule < 0) return;
      const copy: RuleDefinition[] = structuredClone(rules);
      const activeWhen = copy[indexRule].activeWhen;
      if (activeWhen.kind === "group" && activeWhen.op === "OR") {
        copy[indexRule].activeWhen.children = activeWhen.children.filter(
          (_: RuleExpr, i: number) => i !== index,
        );
      } else {
        copy[indexRule].activeWhen = { kind: "group", op: "OR", children: [] };
      }
      putAdapter({
        ...adapter,
        socket_message_priority: {
          ...adapter.socket_message_priority,
          rules: copy,
        },
      });
    },
    [adapter, putAdapter],
  );

  // ************************************************ */
  // USE CALLBACK *********************************** */
  const onClickEdit = useCallback((icon: IconsInterface) => {
    setEditIconId(icon.id);
  }, []);

  const onClickCancel = useCallback(() => {
    setEditIconId("");
  }, []);

  const renderActiveWhen = useCallback(
    (activeWhen: RuleExpr, conditions: ConditionDef[], icon: string, iconObj: IconsInterface) => {
      const byId = new Map(conditions.map((c) => [c.id, c]));
      const labelFor = (condId: string) => byId.get(condId)?.name ?? condId;

      // Normalize: root is always treated as a flat list of OR alternatives.
      // A single ref or AND group at root is wrapped as the sole alternative.
      const alternatives: RuleExpr[] =
        activeWhen.kind === "group" && activeWhen.op === "OR" ? activeWhen.children : [activeWhen];

      const renderConditionChip = (expr: RuleExpr, key: number): React.ReactNode => {
        if (expr.kind !== "ref") return null;
        const conditionDef = getCondition(expr.id, icon, adapter.socket_message_priority.rules);
        return (
          <div
            key={key}
            style={{
              padding: "6px 10px",
              borderRadius: 10,
              marginTop: 6,
              width: "100%",
              textAlign: "center",
              cursor: "pointer",
            }}
            className={`generalStyles__container__${theme}`}
          >
            <div style={{ paddingTop: "5px" }}>
              <Tooltip
                title={<ConditionTooltipContent conditionDef={conditionDef} />}
                overlayInnerStyle={{ width: 315, cursor: "pointer" }}
                placement="right"
              >
                <img
                  style={{ marginRight: "10px", marginTop: "-3px" }}
                  src={iconObj.icon}
                  alt={iconObj.name}
                />
                <span>{labelFor(expr.id)}</span>
              </Tooltip>
            </div>
          </div>
        );
      };

      const renderAlternativeContent = (expr: RuleExpr): React.ReactNode => {
        if (expr.kind === "ref") return renderConditionChip(expr, 0);
        if (expr.kind === "group" && expr.op === "AND") {
          return (
            <div>
              <Tag color="blue" style={{ marginBottom: 6 }}>
                AND
              </Tag>
              {expr.children.map((child, i) => renderConditionChip(child, i))}
            </div>
          );
        }
        return null;
      };

      return (
        <div>
          {alternatives.map((alt, idx) => (
            <div key={idx} className="mt-2">
              <div className="generalStyles__spaceBetween">
                <div className="generalStyles__flexCenter">
                  <Tag color="orange">OR</Tag>
                </div>
                <div>
                  <Tooltip title="Delete">
                    <Button
                      shape="circle"
                      className="buttonStyle__29"
                      onClick={() => onClickDeleteItem(idx, icon)}
                      icon={<FontAwesomeIcon style={{ fontSize: "15px" }} icon={faTrash} />}
                    />
                  </Tooltip>
                </div>
              </div>
              <OrBlock theme={theme}>{renderAlternativeContent(alt)}</OrBlock>
            </div>
          ))}
        </div>
      );
    },
    [onClickDeleteItem, theme, adapter],
  );

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (
      adapter?.socket_message_priority?.rules &&
      adapter.socket_message_priority.rules.length > 0
    ) {
      const rows: { label: string; value: string; icon: string }[] =
        adapter.socket_message_priority.rules.flatMap((rule: RuleDefinition) =>
          (rule.conditions ?? []).map((c) => ({
            label: c.name,
            value: c.id,
            icon: rule.icon,
          })),
        );
      setSelectData(rows);
    }
  }, [adapter]);

  useEffect(() => {
    if (isErrorPutAdapter || isSuccessPutAdapter) {
      setEditIconId("");
      setSelectValues([]);
    }
  }, [isErrorPutAdapter, isSuccessPutAdapter]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const hasActiveRules = (aw: RuleExpr | undefined): boolean => {
    if (!aw) return false;
    if (aw.kind === "ref") return true;
    if (aw.kind !== "group") return false;
    return aw.children.length > 0;
  };

  return (
    <div className="generalStyles__flexWrap" style={{ justifyContent: "center" }}>
      {ICONS.map((icon: IconsInterface) => {
        const ruleForIcon = adapter?.socket_message_priority?.rules.find((r) => r.icon === icon.id);
        return (
          <Card
            key={icon.id}
            title={
              <div>
                <img src={icon.icon} alt={icon.name} />
                <span className="generalStyles__mlFix">{icon.name}</span>
              </div>
            }
            loading={isLoading || isLoadingPutAdapter}
            extra={
              <>
                {editIconId === icon.id ? (
                  <>
                    <Tooltip title={t("general.save")}>
                      <Button
                        shape="circle"
                        className="buttonStyle__10"
                        onClick={() => onClickSave()}
                        icon={<FontAwesomeIcon icon={faSave} />}
                      />
                    </Tooltip>
                    <Tooltip title={t("general.cancel")}>
                      <Button
                        shape="circle"
                        className="buttonStyle__25 generalStyles__mlFix"
                        onClick={() => onClickCancel()}
                        icon={<FontAwesomeIcon icon={faXmark} />}
                      />
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title={t("general.edit")}>
                    <Button
                      shape="circle"
                      className="buttonStyle__11"
                      onClick={() => onClickEdit(icon)}
                      icon={<FontAwesomeIcon icon={faPencil} />}
                    />
                  </Tooltip>
                )}
              </>
            }
            style={{ width: 360, margin: "5px" }}
          >
            {editIconId === icon.id ? (
              <div>
                <Divider className="generalStyles__info">{t("general.activateIconWhen")}</Divider>
                <Select
                  onChange={onChangeSelectActivateIcon}
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Please select"
                  options={selectData.filter((option) => option.icon === icon.id)}
                />
                {/* <Divider className='generalStyles__error'>{t("general.removeIconWhen")}</Divider>
								<Select
									onChange={onChangeSelectRemoveIcon}
									mode='multiple'
									style={{ width: "100%" }}
									placeholder='Please select'
									options={selectData.filter((option) => option.icon === icon.id)}
								/> */}
              </div>
            ) : (
              <div>
                {ruleForIcon && hasActiveRules(ruleForIcon.activeWhen) ? (
                  <div style={{ marginTop: 6 }}>
                    {" "}
                    <Divider className="generalStyles__info">
                      {t("general.activateIconWhen")}
                    </Divider>
                    {renderActiveWhen(
                      ruleForIcon.activeWhen,
                      ruleForIcon.conditions,
                      ruleForIcon.icon,
                      icon,
                    )}
                    {/* <Divider className="generalStyles__error">
                      {t("general.removeIconWhen")}
                    </Divider> */}
                    {/* <div className='mt-2'>
											<OrBlock theme={theme}>
												<div
													style={{
														padding: "6px 10px",
														borderRadius: 10,
														marginTop: 6,
														width: "100%",
														gap: 8,
														textAlign: "center",
														cursor: "pointer",
													}}
													className={`generalStyles__container__${theme}`}
												>
													<div style={{ paddingTop: "5px" }}>
														<Tooltip
															title={
																<ConditionTooltipContent
																	conditionDef={getCondition(
																		ruleForIcon?.removeWhen?.id[0] || "",
																		ruleForIcon?.icon,
																		adapter.socket_message_priority.rules,
																	)}
																/>
															}
															overlayInnerStyle={{
																width: 315,
																cursor: "pointer",
															}}
															placement='right'
														>
															<span>
																{getConditionName(
																	ruleForIcon?.removeWhen?.id[0] || "",
																	ruleForIcon?.icon,
																	adapter.socket_message_priority.rules,
																)}
															</span>
														</Tooltip>
													</div>
												</div>
											</OrBlock>
										</div> */}
                  </div>
                ) : (
                  <div style={{ opacity: 0.7, textAlign: "center" }}>
                    No rule configured for this icon.
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}

      <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: 16 }}>
        <Button
          danger
          onClick={onClickResetAll}
          icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
        >
          {t("general.resetAllConfiguration")}
        </Button>
      </div>

      <Modal
        open={isResetModalOpen}
        onOk={onConfirmReset}
        onCancel={() => setIsResetModalOpen(false)}
        okText={t("general.confirm")}
        cancelText={t("general.cancel")}
        okButtonProps={{ danger: true }}
        confirmLoading={isLoadingPutAdapter}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "8px 0" }}>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            style={{ fontSize: 24, color: "#faad14", marginTop: 2, flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8, marginLeft: "10px" }}>
              {t("general.resetAllConfiguration")}
            </div>
            <div>{t("general.resetAllConfigurationDescription")}</div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
